import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  Progress,
  Spinner,
} from "reactstrap";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  FiBriefcase,
  FiCheckSquare,
  FiAlertCircle,
  FiUsers,
  FiFilePlus,
  FiTrendingUp,
  FiClock,
} from "react-icons/fi";
import "./Dashboard.css";
import { getAllApplications, getAllJobs } from "../../services/api";

const PIE_COLORS = ["#3b82f6", "#f59e0b", "#10b981", "#8b5cf6", "#22c55e", "#ef4444"];

function Dashboard() {
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [jobsData, appsData] = await Promise.all([
          getAllJobs(),
          getAllApplications(),
        ]);

        setJobs(Array.isArray(jobsData) ? jobsData : []);
        setApplications(Array.isArray(appsData) ? appsData : []);
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ========== Derived Stats ==========
  const totalJobs = jobs.length;
  const activeJobs = jobs.filter((j) => j.status === "Published").length;
  const closedJobs = jobs.filter(
    (j) => j.status === "Closed" || j.status === "Expired" || j.status === "Draft"
  ).length;
  const totalApplicants = applications.length;
  const newApplications = applications.filter((a) => a.status === "New").length;

  // Applicants count per job
  const jobApplicantCount = useMemo(() => {
    const map = {};
    applications.forEach((app) => {
      map[app.jobId] = (map[app.jobId] || 0) + 1;
    });
    return map;
  }, [applications]);

  // Active jobs with applicant count
  const activeJobsList = useMemo(() => {
    return jobs
      .filter((j) => j.status === "Published")
      .map((job) => ({
        id: job.id,
        title: job.title,
        department: job.department,
        location: job.location,
        applicantsCount: jobApplicantCount[job.id] || 0,
      }));
  }, [jobs, jobApplicantCount]);

  // Recent applications (latest 5)
  const recentApplicants = useMemo(() => {
    return [...applications]
      .sort((a, b) => new Date(b.appliedAt) - new Date(a.appliedAt))
      .slice(0, 5)
      .map((app) => ({
        id: app.id,
        name: app.fullName?.trim() || "Unknown",
        email: app.email,
        jobTitle: app.Job?.title || "N/A",
        experience: app.totalExperience?.trim() || "-",
        appliedDate: new Date(app.appliedAt).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
        status: app.status || "New",
      }));
  }, [applications]);

  // Status distribution for pie + pipeline
  const statusCounts = useMemo(() => {
    const statuses = [
      "New",
      "Under Review",
      "Shortlisted",
      "Interview Scheduled",
      "Selected",
      "Rejected",
    ];

    return statuses
      .map((status) => ({
        name: status,
        value: applications.filter((a) => a.status === status).length,
      }))
      .filter((item) => item.value > 0);
  }, [applications]);

  // ========== Loading & Error States ==========
  if (loading) {
    return (
      <div className="dashboard-page-wrapper d-flex align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
        <div className="text-center">
          <Spinner color="primary" />
          <p className="mt-3 text-muted">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page-wrapper d-flex align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
        <div className="text-center">
          <h5 className="text-danger mb-3">{error}</h5>
          <Button color="primary" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page-wrapper py-2 py-lg-3">
      <Container className="dashboard-container p-0">
        {/* Header */}
        <Row className="mb-5 align-items-center justify-content-between">
          <Col>
            <h1 className="dashboard-main-title">Dashboard Overview</h1>
            <p className="dashboard-subtitle text-muted">
              Your recruitment snapshot • Updated real-time
            </p>
          </Col>
          <Col xs="auto">
            <Button
              color="success"
              className="post-job-btn"
              onClick={() => navigate("/admin/careers")}
            >
              <FiFilePlus className="me-2" size={18} />
              Post New Job
            </Button>
          </Col>
        </Row>

        {/* Stats Cards */}
        <Row className="mb-5 g-3 g-md-4 stats-row">
          {[
            { label: "Total Jobs", value: totalJobs, icon: FiBriefcase, topColor: "#3b82f6" },
            { label: "Active Jobs", value: activeJobs, icon: FiCheckSquare, topColor: "#10b981" },
            { label: "Closed Jobs", value: closedJobs, icon: FiAlertCircle, topColor: "#ef4444" },
            { label: "Total Applicants", value: totalApplicants, icon: FiUsers, topColor: "#6366f1" },
            { label: "New Applications", value: newApplications, icon: FiFilePlus, topColor: "#10b981" },
          ].map((card) => (
            <Col key={card.label} xs={6} sm={6} md={4} lg={3} xl>
              <Card className="dashboard-stat-card shadow-sm border-0">
                <div className="top-border-strip" style={{ backgroundColor: card.topColor }} />
                <CardBody className="text-center pt-4 pb-4">
                  <div
                    className="stat-icon-circle mb-3 mx-auto"
                    style={{
                      backgroundColor: `${card.topColor}1f`,
                      color: card.topColor,
                    }}
                  >
                    <card.icon size={28} />
                  </div>
                  <h3 className="stat-number1 mb-1">{card.value}</h3>
                  <p className="stat-text">{card.label}</p>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts & Pipeline */}
        <Row className="mb-5 g-4 analytics-row">
          {/* Pie Chart */}
          <Col lg={4}>
            <Card className="chart-card shadow-hover border-0">
              <CardBody>
                <CardTitle tag="h5" className="chart-title mb-4">
                  Application Status Distribution
                </CardTitle>

                {statusCounts.length === 0 ? (
                  <div className="text-center text-muted py-5">No applications yet</div>
                ) : (
                  <>
                    <div className="pie-chart-container">
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie
                            data={statusCounts}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={85}
                            paddingAngle={4}
                            dataKey="value"
                          >
                            {statusCounts.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={PIE_COLORS[index % PIE_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              background: "#c7ced8ff",
                              border: "none",
                              borderRadius: "8px",
                              color: "#e2e8f0",
                              padding: "10px 14px",
                              fontSize: "13px",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="status-legend mt-3">
                      {statusCounts.map((entry, index) => (
                        <div key={entry.name} className="legend-item">
                          <div
                            className="legend-color-dot"
                            style={{
                              backgroundColor: PIE_COLORS[index % PIE_COLORS.length],
                            }}
                          />
                          <span className="legend-text">{entry.name}</span>
                          <span className="legend-count ms-auto">{entry.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Pipeline + Active Jobs */}
          <Col lg={8}>
            <Row className="g-4 h-100">
              {/* Hiring Pipeline */}
              <Col md={6}>
                <Card className="pipeline-card shadow-hover border-0 h-100">
                  <CardBody>
                    <div className="d-flex align-items-center mb-4">
                      <FiClock size={20} className="text-info me-2" />
                      <CardTitle tag="h5" className="mb-0">
                        Hiring Pipeline
                      </CardTitle>
                    </div>

                    {statusCounts.length === 0 ? (
                      <div className="text-center text-muted py-4">No data available</div>
                    ) : (
                      <div className="pipeline-bars">
                        {statusCounts.map((s, i) => {
                          const percent =
                            totalApplicants > 0
                              ? (s.value / totalApplicants) * 100
                              : 0;
                          return (
                            <div key={s.name} className="pipeline-item mb-3">
                              <div className="d-flex justify-content-between mb-1 small">
                                <span className="text-muted">{s.name}</span>
                                <strong>{s.value}</strong>
                              </div>
                              <Progress
                                value={percent}
                                color={
                                  ["primary", "warning", "success", "info", "success", "danger"][i]
                                }
                                className="pipeline-progress"
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>

              {/* Active Positions */}
              <Col md={6}>
                <Card className="active-jobs-card shadow-hover border-0 h-100">
                  <CardBody>
                    <div className="d-flex align-items-center mb-4">
                      <FiTrendingUp size={20} className="text-success me-2" />
                      <CardTitle tag="h5" className="mb-0">
                        Active Positions
                      </CardTitle>
                    </div>

                    <div className="active-jobs-scroll">
                      {activeJobsList.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          No active jobs
                        </div>
                      ) : (
                        activeJobsList.map((job) => (
                          <div key={job.id} className="active-job-row">
                            <div className="job-info">
                              <p className="job-title mb-0">{job.title}</p>
                              <small className="text-muted">
                                {job.department} • {job.location}
                              </small>
                            </div>
                            <div className="job-stats text-end">
                              <strong className="applicants-count">
                                {job.applicantsCount}
                              </strong>
                              <small className="text-muted d-block">
                                applicants
                              </small>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Recent Applications Table */}
        <Card className="recent-apps-card shadow-hover border-0">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <CardTitle tag="h5" className="mb-0">
                Recent Applications
              </CardTitle>
              <Button
                color="link"
                className="view-all-btn"
                onClick={() => navigate("/admin/applicants")}
              >
                View All →
              </Button>
            </div>

            {recentApplicants.length === 0 ? (
              <div className="text-center text-muted py-5">
                No applications received yet
              </div>
            ) : (
              <Table responsive hover className="recent-table mb-0">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Email</th>
                    <th>Position</th>
                    <th>Experience</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentApplicants.map((app) => (
                    <tr key={app.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="applicant-avatar me-3">
                            {app.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                          <span className="applicant-name">{app.name}</span>
                        </div>
                      </td>
                      <td className="text-muted">{app.email}</td>
                      <td>{app.jobTitle}</td>
                      <td className="text-muted">{app.experience}</td>
                      <td className="text-muted">{app.appliedDate}</td>
                      <td>
                        <span
                          className={`status-badge status-${app.status
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                        >
                          {app.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}

export default Dashboard;